import React, { createContext, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useSSE } from '../hooks/useSSE';
import logger from '../utils/logger';


export const ApiContext = createContext();

export const ApiProvider = ({ children }) => {
  const auth = useAuth();
  const { token, logout, user } = auth;

  // El backend expone las rutas bajo /api/admin - usar ruta relativa
  const baseURL = process.env.REACT_APP_API_BASE_URL || '/api/admin';

  const rawAgentId = token ? (user?.username ?? user?.id ?? '') : '';
  const agentId =
    typeof rawAgentId === 'string'
      ? rawAgentId.trim()
      : rawAgentId
        ? String(rawAgentId)
        : '';
  const encodedAgentId = agentId ? encodeURIComponent(agentId) : '';
  const sseUrl = encodedAgentId ? `${baseURL}/sse/events?agentId=${encodedAgentId}` : `${baseURL}/sse/events`;

  // SSE connection for real-time updates - only when authenticated
  logger.log('[API] SSE URL:', sseUrl, 'token exists:', !!token);

  const sse = useSSE(token ? sseUrl : null, {
    onOpen: () => logger.log('[API] SSE connected'),
    onError: (error) => console.error('[API] SSE error:', error),
    onClose: () => logger.log('[API] SSE disconnected'),
    autoReconnect: !!token, // Only auto-reconnect when authenticated
    reconnectInterval: 3000
  });

  // Disconnect SSE when token is removed
  useEffect(() => {
    if (!token && sse) {
      logger.log('[API] Token removed, disconnecting SSE');
      sse.disconnect();
    }
  }, [token, sse]);

  const apiCall = async (endpoint, options = {}) => {
    const url = `${baseURL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
    logger.log('[API] Making request to:', url, 'with options:', options);

    const config = {
      method: options.method || 'GET',
      headers: {
        ...(options.headers || {})
      },
      credentials: 'include',
      ...options,
    };

    // Evitar forzar Content-Type cuando enviamos FormData
    const isFormData = options?.body instanceof FormData;
    if (!isFormData) {
      config.headers['Content-Type'] = config.headers['Content-Type'] || 'application/json';
    }

    // Agregar token si existe
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Identificar si la petición viene de las vistas de supervisión
    const pathName = typeof window !== 'undefined' ? window.location.pathname : '';
    if (pathName.includes('/supervisor') || pathName.includes('/supervision')) {
      config.headers['X-Request-Source'] = 'supervisor';
    }

    try {
      const response = await fetch(url, config);

      if (response.status === 401) {
        // token inválido/expirado → reportar pero NO auto-logout (solo los flujos permitidos deben cerrar sesión)
        logger.warn(`[API] 401 en ${endpoint} - dejando sesión intacta`);
        throw new Error('Sesión expirada');
      }

      let data = null;
      try {
        data = await response.json();
      } catch (parseErr) {
        data = null;
      }
      const isSoftphoneEndpoint = endpoint.startsWith('/softphone/');
      if (!response.ok) {
        if (isSoftphoneEndpoint) {
          logger.warn('[API][SOFTPHONE] Response error', {
            endpoint,
            status: response.status,
            data
          });
        }
        throw new Error(data?.message || `Error ${response.status}`);
      }
      if (isSoftphoneEndpoint) {
        logger.log('[API][SOFTPHONE] Response ok', {
          endpoint,
          status: response.status,
          hasData: !!data,
          keys: data ? Object.keys(data) : []
        });
      }
      return data;
    } catch (error) {
      console.error(`API call error (${endpoint}):`, error);
      throw error;
    }
  };

  // --------- AGENTS ---------
  const agents = {
    getAll: (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return apiCall(`/agents${qs ? `?${qs}` : ''}`);
    },
    getById: (agent) => apiCall(`/agents/${agent}`),
    updateProfile: (agent, payload) => {
      const isFormData = payload instanceof FormData;
      const options = {
        method: 'PUT',
        body: isFormData ? payload : JSON.stringify(payload)
      };

      if (!isFormData) {
        options.headers = { 'Content-Type': 'application/json' };
      }

      return apiCall(`/agents/${agent}/profile`, options);
    },
    create: (agentData) => apiCall('/agents', { method: 'POST', body: JSON.stringify(agentData) }),
    bulkImport: (agentsPayload) => apiCall('/agents/bulk', { method: 'POST', body: JSON.stringify({ agents: agentsPayload }) }),
    createVirtual: (virtualAgentData) => apiCall('/agents/virtual', { method: 'POST', body: JSON.stringify(virtualAgentData) }),
    updateVirtual: (agent, virtualAgentData) => apiCall(`/agents/virtual/${agent}`, { method: 'PUT', body: JSON.stringify(virtualAgentData) }),
    powerOnVirtual: (agent) => apiCall(`/agents/virtual/${agent}/power-on`, { method: 'POST' }),
    powerOffVirtual: (agent) => apiCall(`/agents/virtual/${agent}/power-off`, { method: 'POST' }),
    update: (agent, agentData) => apiCall(`/agents/${agent}`, { method: 'PUT', body: JSON.stringify(agentData) }),
    changePassword: (agent, password) => apiCall(`/agents/${agent}/password`, { method: 'PUT', body: JSON.stringify({ password }) }),
    delete: (agent) => apiCall(`/agents/${agent}`, { method: 'DELETE' }),
    
    // Agent-Skill assignments
    getSkills: (agent) => apiCall(`/agents/${agent}/skills`),
    assignSkill: (agent, skillData) => apiCall(`/agents/${agent}/skills`, { method: 'POST', body: JSON.stringify(skillData) }),
    updateSkill: (agent, id_skill, skillData) => apiCall(`/agents/${agent}/skills/${id_skill}`, { method: 'PUT', body: JSON.stringify(skillData) }),
    unassignSkill: (agent, id_skill) => apiCall(`/agents/${agent}/skills/${id_skill}`, { method: 'DELETE' }),
    bulkSkillOperations: (operations) => apiCall('/agents/agent-skills/bulk', { method: 'POST', body: JSON.stringify({ operations }) }),
    getCrmConfigs: (agent) => apiCall(`/agents/${agent}/crm-configs`),
    createCrmSession: (agent, id_skill) => apiCall(`/agents/${agent}/crm-configs/${id_skill}/session`, { method: 'POST' }),

    // Agent status management
    updateStatus: (agent, statusData) => {
      logger.log('[API] updateStatus called with:', { agent, statusData });
      return apiCall(`/agents/${agent}/status`, { method: 'PUT', body: JSON.stringify(statusData) });
    },
    forceDisconnect: (agent) => apiCall(`/agents/${agent}/disconnect`, { method: 'POST' }),

    // Available extensions
    getAvailableExtensions: () => apiCall('/agents/available-extensions'),
    getDailyStats: (agentId, params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return apiCall(`/agents/${agentId}/daily-stats${qs ? `?${qs}` : ''}`);
    },
    getDailySummary: (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return apiCall(`/agents/reports/daily-summary${qs ? `?${qs}` : ''}`);
    },
    getStatusBoard: (params = {}) => {
      const sanitized = Object.entries(params || {}).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {});
      const qs = new URLSearchParams(sanitized).toString();
      return apiCall(`/agents/status-board${qs ? `?${qs}` : ''}`);
    },
    exportAll: async () => {
      const url = `${baseURL}/agents/export`;
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const response = await fetch(url, {
        method: 'GET',
        headers,
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('No se pudo exportar agentes');
      }
      return response.blob();
    },
    downloadImportTemplate: async () => {
      const url = `${baseURL}/agents/bulk/template`;
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const response = await fetch(url, {
        method: 'GET',
        headers,
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('No se pudo descargar la plantilla de agentes');
      }
      return response.blob();
    }
  };

// --------- AMD ----------

const amd = {
  getAll: () => apiCall('/amd'),
  get: (id) => apiCall(`/amd/${id}`),
  create: (data) => apiCall('/amd', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/amd/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id) => apiCall(`/amd/${id}`, { method: 'DELETE' }),
};
// --------- SKILLS ----------
const skills = {
  getAll: () => apiCall('/skills'),
  getById: (id) => apiCall(`/skills/${id}`),
  getOne: (id) => apiCall(`/skills/${id}`),
  create: (payload) =>
    apiCall('/skills', { method: 'POST', body: JSON.stringify(payload) }),
  update: (id, payload) =>
    apiCall(`/skills/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  delete: (id) => apiCall(`/skills/${id}`, { method: 'DELETE' }),
};


    // --------- EXTENSIONS ---------
  const extensions = {
    getAll: (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return apiCall(`/extensions${qs ? `?${qs}` : ''}`); // {success, data:{extensions:[...], pagination?}}
    },
    create: (extData) => apiCall('/extensions', { method: 'POST', body: JSON.stringify(extData) }),
    createBulk: (bulkData) => apiCall('/extensions/bulk', { method: 'POST', body: JSON.stringify(bulkData) }),
    update: (exten, extData) => apiCall(`/extensions/${exten}`, { method: 'PUT', body: JSON.stringify(extData) }),
    delete: (exten) => apiCall(`/extensions/${exten}`, { method: 'DELETE' }),
  };

// --------- DATA STRUCTURES ----------
const datastructures = {
  // estructuras
  getAll: () => apiCall('/datastructures'),
  create: (nombre) =>
    apiCall('/datastructures', { method: 'POST', body: JSON.stringify({ nombre }) }),
  update: (id, payload) =>
    apiCall(`/datastructures/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  delete: (id) => apiCall(`/datastructures/${id}`, { method: 'DELETE' }),

  // campos de una estructura
  getFields: (id) => apiCall(`/datastructures/${id}/fields`),
  createField: (id, field) =>
    apiCall(`/datastructures/${id}/fields`, { method: 'POST', body: JSON.stringify(field) }),
  updateField: (id, fieldId, field) =>
    apiCall(`/datastructures/${id}/fields/${fieldId}`, { method: 'PUT', body: JSON.stringify(field) }),
  deleteField: (id, fieldId) =>
    apiCall(`/datastructures/${id}/fields/${fieldId}`, { method: 'DELETE' }),
};

// --------- CAMPAIGNS ----------
const campaigns = {
  getAll: () => apiCall('/campaigns'),
  create: (payload) => apiCall('/campaigns', { method: 'POST', body: JSON.stringify(payload) }),
  update: (idcampaign, payload) => apiCall(`/campaigns/${idcampaign}`, { method: 'PUT', body: JSON.stringify(payload) }),
  delete: (idcampaign) => apiCall(`/campaigns/${idcampaign}`, { method: 'DELETE' }),
  // Asignación de campañas por usuario
  getAssignments: () => apiCall('/campaigns/assignments'),
  getAgentAssignments: (agentId) => apiCall(`/campaigns/assignments/${agentId}`),
  setAgentAssignments: (agentId, campaignIds) => apiCall(`/campaigns/assignments/${agentId}`, {
    method: 'PUT',
    body: JSON.stringify({ campaignIds })
  }),
};

// --------- TRUNKS ----------
const trunks = {
  getAll: () => apiCall('/trunks'),
  get: (id) => apiCall(`/trunks/${id}`),
  create: (data) => apiCall('/trunks', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/trunks/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id) => apiCall(`/trunks/${id}`, { method: 'DELETE' }),
  deploy: () => apiCall('/trunks/deploy', { method: 'POST' }),
};

// --------- dialplans ----------
const dialplans = {
  getAll: () => apiCall('/dialplans'),
  get: (id) => apiCall(`/dialplans/${id}`),
  create: (data) => apiCall('/dialplans', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/dialplans/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id) => apiCall(`/dialplans/${id}`, { method: 'DELETE' }),
};

// --------- API TOKENS ----------
const apiTokens = {
  getAll: () => apiCall('/api-tokens'),
  getById: (id) => apiCall(`/api-tokens/${id}`),
  create: (data) => apiCall('/api-tokens', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/api-tokens/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/api-tokens/${id}`, { method: 'DELETE' }),
  regenerate: (id) => apiCall(`/api-tokens/${id}/regenerate`, { method: 'POST' }),
};

// --------- WEBHOOKS ----------
const webhooksApi = {
  getAll: () => apiCall('/webhooks'),
  getById: (id) => apiCall(`/webhooks/${id}`),
  getTypes: () => apiCall('/webhooks/types'),
  getLoads: (skillId) => apiCall(`/webhooks/loads/${skillId}`),
  create: (data) => apiCall('/webhooks', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/webhooks/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiCall(`/webhooks/${id}`, { method: 'DELETE' }),
};

// --------- LOADS ----------
const loads = {
  getAll: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/loads${qs ? `?${qs}` : ''}`);
  },
  getById: (id) => apiCall(`/loads/${id}`),
  create: (formData) => apiCall('/loads', {
    method: 'POST',
    body: formData,
    headers: {} // No Content-Type para FormData
  }),
  delete: (id) => apiCall(`/loads/${id}`, { method: 'DELETE' }),
  setActivationState: (id, active) => apiCall(`/loads/${id}/activation`, {
    method: 'POST',
    body: JSON.stringify({ active })
  }),
  bulkSetActivationState: (ids, active) => apiCall('/loads/bulk/activation', {
    method: 'POST',
    body: JSON.stringify({ ids, active })
  }),
  bulkDelete: (ids) => apiCall('/loads/bulk/delete', {
    method: 'POST',
    body: JSON.stringify({ ids })
  }),
  getCampaignSkills: (campaignId) => apiCall(`/loads/campaigns/${campaignId}/skills`),
  getSkillStructure: (skillId) => apiCall(`/loads/skills/${skillId}/structure`),
  downloadTemplate: async (skillId) => {
    const url = `${baseURL}/loads/skills/${skillId}/template`;
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const response = await fetch(url, {
      method: 'GET',
      headers,
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('No se pudo descargar la plantilla');
    }
    return response.blob();
  }
};

// --------- DIALER / ENGINES ----------
const dialer = {
  getEngines: () => apiCall('/dialer/engines'),
  getEngine: (skillDef) => apiCall(`/dialer/skills/${encodeURIComponent(skillDef)}/engine`),
  startSkill: (skillDef) =>
    apiCall(`/dialer/skills/${encodeURIComponent(skillDef)}/start`, { method: 'POST' }),
  stopSkill: (skillDef) =>
    apiCall(`/dialer/skills/${encodeURIComponent(skillDef)}/stop`, { method: 'POST' }),
  getAsteriskChannels: () => apiCall('/dialer/asterisk/channels-summary'),
  getAsteriskQueues: () => apiCall('/dialer/asterisk/queues-summary'),
  resetAsteriskQueues: (queue = null) =>
    apiCall('/dialer/asterisk/queues-reset', {
      method: 'POST',
      body: JSON.stringify(queue ? { queue } : {})
    })
};

// --------- SOFTPHONE ----------
const softphone = {
  getConfig: () => apiCall('/softphone/config'), // Gets PBX IP configuration
  getAgentExtension: (agent) => apiCall(`/softphone/agent/${agent}/extension`), // Gets extension from cti_agent by agent
  getAgentExtensionByUsername: (username) => apiCall(`/softphone/user/${username}/extension`), // Gets extension from cti_agent by username
  getExtensionCredentials: (extension) => apiCall(`/softphone/extension/${extension}/credentials`), // Gets password from cti_extension
  getSkillData: (skill, dataId) => apiCall(`/softphone/skill-data/${encodeURIComponent(skill)}/${encodeURIComponent(dataId)}`),
  getCallMetadata: (actionId) => apiCall(`/softphone/call-metadata/${encodeURIComponent(actionId)}`),
  getActiveCall: (agent) =>
    apiCall(`/softphone/active-call/${encodeURIComponent(agent)}`)
      .then((res) => res?.data ?? res)
};

// --------- CRM CONFIGS ----------
const crmConfigs = {
  list: () => apiCall('/crm-configs'),
  create: (data) => apiCall('/crm-configs', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/crm-configs/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
};

// --------- ENDS (FINALES) ----------
const ends = {
  // Groups
  getGroups: async (params = {}) => {
    const { cache = false, forceRefresh = false, ...query } = params || {};
    const qs = new URLSearchParams(query).toString();
    const key = `cti_ends_groups_${qs || 'default'}`;

    if (cache && !forceRefresh) {
      try {
        const cached = sessionStorage.getItem(key);
        if (cached) {
          logger.log('[ENDS][getGroups] Datos obtenidos desde sessionStorage (CACHÉ)', { cacheKey: key });
          return { success: true, data: JSON.parse(cached), fromCache: true };
        }
      } catch (e) {
        logger.warn('[ENDS][getGroups] Error leyendo cache', e);
      }
    }

    const result = await apiCall(`/ends/groups${qs ? `?${qs}` : ''}`);
    if (cache && result?.data) {
      try {
        sessionStorage.setItem(key, JSON.stringify(result.data));
        logger.log('[ENDS][getGroups] Datos guardados en sessionStorage', { cacheKey: key });
      } catch (e) {
        logger.warn('[ENDS][getGroups] Error guardando cache', e);
      }
    }
    return result;
  },
  getGroup: (id) => apiCall(`/ends/groups/${id}`),
  createGroup: (data) => apiCall('/ends/groups', { method: 'POST', body: JSON.stringify(data) }),
  updateGroup: (id, data) => apiCall(`/ends/groups/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteGroup: (id) => apiCall(`/ends/groups/${id}`, { method: 'DELETE' }),

  // Typifications
  getTree: async (groupId, params = {}) => {
    const { cache = false, forceRefresh = false, skillDef = null, ...query } = params || {};
    const qs = new URLSearchParams(query).toString();
    const key = `cti_ends_tree_${groupId}_${qs || 'default'}`;

    logger.log('[ENDS][getTree] Iniciando carga de finales', {
      groupId,
      skillDef,
      cache,
      forceRefresh,
      cacheKey: key
    });

    if (cache && !forceRefresh) {
      try {
        const cached = sessionStorage.getItem(key);
        if (cached) {
          logger.log('[ENDS][getTree] Finales obtenidos desde sessionStorage (CACHÉ)', {
            groupId,
            skillDef,
            cacheKey: key,
            itemsCount: JSON.parse(cached).length
          });
          return { success: true, data: JSON.parse(cached), fromCache: true };
        } else {
          logger.log('[ENDS][getTree] No hay datos en sessionStorage, consultando servidor', {
            groupId,
            skillDef,
            cacheKey: key
          });
        }
      } catch (err) {
        logger.warn('[ENDS][getTree] Error leyendo caché, consultando servidor', {
          groupId,
          skillDef,
          error: err.message
        });
      }
    } else if (forceRefresh) {
      logger.log('[ENDS][getTree] ForceRefresh activado, eliminando caché y consultando servidor', {
        groupId,
        skillDef
      });
      try {
        sessionStorage.removeItem(key);
      } catch (err) {
        /* ignore */
      }
    }

    logger.log('[ENDS][getTree] Consultando servidor para obtener finales', {
      groupId,
      skillDef,
      url: `/ends/groups/${groupId}/tree${qs ? `?${qs}` : ''}`
    });

    const resp = await apiCall(`/ends/groups/${groupId}/tree${qs ? `?${qs}` : ''}`);

    if (resp?.success && cache) {
      try {
        sessionStorage.setItem(key, JSON.stringify(resp.data || []));
        logger.log('[ENDS][getTree] Finales guardados en sessionStorage', {
          groupId,
          skillDef,
          cacheKey: key,
          itemsCount: (resp.data || []).length
        });
      } catch (err) {
        logger.warn('[ENDS][getTree] Error guardando caché', {
          groupId,
          skillDef,
          error: err.message
        });
      }
    }

    logger.log('[ENDS][getTree] Finales obtenidos desde SERVIDOR', {
      groupId,
      skillDef,
      itemsCount: (resp.data || []).length,
      fromCache: false
    });

    return { ...resp, fromCache: false };
  },
  createTypification: (groupId, data) => apiCall(`/ends/groups/${groupId}/typifications`, { method: 'POST', body: JSON.stringify(data) }),
  updateTypification: (id, data) => apiCall(`/ends/typifications/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteTypification: (id) => apiCall(`/ends/typifications/${id}`, { method: 'DELETE' }),

  // Save typification result
  saveTypification: (data) => apiCall('/ends/save-typification', { method: 'POST', body: JSON.stringify(data) })
};

// --------- HEARTBEAT ----------
const heartbeat = {
  ping: (agentId, sessionId, extra = {}) => {
    const payload = { agentId, sessionId, ...extra };
    return apiCall('/heartbeat/ping', { method: 'POST', body: JSON.stringify(payload) });
  },
  logout: (agentId, sessionId) => apiCall('/heartbeat/logout', { method: 'POST', body: JSON.stringify({ agentId, sessionId }) }),
  getStatus: (agentId) => apiCall(`/heartbeat/status/${agentId}`),
  getConnected: () => apiCall('/heartbeat/connected'),
  getStats: () => apiCall('/heartbeat/stats'),
  syncStatus: (agentId) => apiCall('/heartbeat/sync-status', { method: 'POST', body: JSON.stringify({ agentId }) })
};

// --------- ROLES ----------
const roles = {
  getAll: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/roles${qs ? `?${qs}` : ''}`);
  },
  getById: (id) => apiCall(`/roles/${id}`),
  create: (roleData) => apiCall('/roles', { method: 'POST', body: JSON.stringify(roleData) }),
  update: (id, roleData) => apiCall(`/roles/${id}`, { method: 'PUT', body: JSON.stringify(roleData) }),
  delete: (id) => apiCall(`/roles/${id}`, { method: 'DELETE' }),
  getPermissions: () => apiCall('/roles/permissions/all'),
  assignToAgent: (agentId, roleId) => apiCall('/roles/assign', { method: 'POST', body: JSON.stringify({ agentId, roleId }) })
};

// --------- WORKFLOWS ----------
const workflows = {
  list: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/workflows${qs ? `?${qs}` : ''}`);
  },
  get: (id) => apiCall(`/workflows/${id}`),
  create: (data) => apiCall('/workflows', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiCall(`/workflows/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id) => apiCall(`/workflows/${id}`, { method: 'DELETE' }),
  test: (id, data) => apiCall(`/workflows/${id}/test`, { method: 'POST', body: JSON.stringify(data) }),
  getExecutions: (id, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/workflows/${id}/executions${qs ? `?${qs}` : ''}`);
  },
  stats: () => apiCall('/workflows/stats/overview')
};

// --------- SIMULATOR ----------
const simulator = {
  getStatus: () => apiCall('/simulator/status'),
  addAgent: (payload) => apiCall('/simulator/agents', { method: 'POST', body: JSON.stringify(payload) }),
  updateAgent: (id, state) => apiCall(`/simulator/agents/${id}`, { method: 'PUT', body: JSON.stringify({ state }) }),
  deleteAgent: (id) => apiCall(`/simulator/agents/${id}`, { method: 'DELETE' }),
  uploadDataset: (records) => apiCall('/simulator/dataset', { method: 'POST', body: JSON.stringify({ records }) }),
  start: (config) => apiCall('/simulator/control/start', { method: 'POST', body: JSON.stringify(config) }),
  stop: () => apiCall('/simulator/control/stop', { method: 'POST' }),
  reset: () => apiCall('/simulator/control/reset', { method: 'POST' })
};

const supervisor = {
  getSkillMetrics: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {})).toString();
    return apiCall(`/supervisor/skills/metrics${qs ? `?${qs}` : ''}`);
  },
  refreshSkillMetrics: (campaignId = null) =>
    apiCall('/supervisor/skills/metrics/refresh', {
      method: 'POST',
      body: JSON.stringify({ campaignId })
    }),
  getEndGroups: (skillDef, loadIds = []) => {
    const params = new URLSearchParams();
    (loadIds || []).forEach((id) => {
      if (id !== undefined && id !== null && id !== '') {
        params.append('loadIds', id);
      }
    });
    const qs = params.toString();
    return apiCall(`/supervisor/skills/${encodeURIComponent(skillDef)}/end-groups${qs ? `?${qs}` : ''}`);
  },
  resetSkillRecords: (skillDef, payload) =>
    apiCall(`/supervisor/skills/${encodeURIComponent(skillDef)}/reset`, {
      method: 'POST',
      body: JSON.stringify(payload)
    }),
  getQueueMetrics: (params = {}) => {
    const search = new URLSearchParams();
    if (params.campaignId) {
      search.append('campaignId', params.campaignId);
    }
    if (Array.isArray(params.skillDefs)) {
      params.skillDefs.forEach((skill) => {
        if (skill) search.append('skillDefs', skill);
      });
    } else if (typeof params.skillDefs === 'string') {
      params.skillDefs.split(',').forEach((skill) => {
        const trimmed = skill.trim();
        if (trimmed) search.append('skillDefs', trimmed);
      });
    }
    if (Array.isArray(params.loadIds)) {
      params.loadIds.forEach((id) => {
        if (id !== undefined && id !== null && id !== '') {
          search.append('loadIds', id);
        }
      });
    } else if (typeof params.loadIds === 'string') {
      params.loadIds.split(',').forEach((id) => {
        const trimmed = id.trim();
        if (trimmed) search.append('loadIds', trimmed);
      });
    }
    const qs = search.toString();
    return apiCall(`/supervisor/queues/metrics${qs ? `?${qs}` : ''}`);
  },
  refreshQueueMetrics: (filters = {}) =>
    apiCall('/supervisor/queues/metrics/refresh', {
      method: 'POST',
      body: JSON.stringify(filters)
    }),
  listenToAgent: (agentId, payload = {}) =>
    apiCall(`/supervisor/agents/${encodeURIComponent(agentId)}/listen`, {
      method: 'POST',
      body: JSON.stringify(payload)
    }),
  whisperToAgent: (agentId, payload = {}) =>
    apiCall(`/supervisor/agents/${encodeURIComponent(agentId)}/whisper`, {
      method: 'POST',
      body: JSON.stringify(payload)
    }),
  conferenceWithAgent: (agentId, payload = {}) =>
    apiCall(`/supervisor/agents/${encodeURIComponent(agentId)}/conference`, {
      method: 'POST',
      body: JSON.stringify(payload)
    })
};

const systemConfig = {
  get: () => apiCall('/config/system'),
  update: (payload) => apiCall('/config/system', { method: 'PUT', body: JSON.stringify(payload) }),
  getTimezones: () => apiCall('/config/timezones')
};

// --------- MULTIMEDIA ---------
const multimedia = {
  // Browse directory contents
  browse: (type, path = '') => {
    const params = path ? `?path=${encodeURIComponent(path)}` : '';
    return apiCall(`/multimedia/browse/${type}${params}`);
  },

  // Download file
  download: async (type, path, asBlob = false) => {
    const url = `${baseURL}/multimedia/download/${type}?path=${encodeURIComponent(path)}`;
    const config = {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` })
      },
      credentials: 'include'
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = 'Error descargando archivo';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          // If response is not JSON, use default message
        }
        throw new Error(errorMessage);
      }

      if (asBlob) {
        return await response.blob();
      } else {
        // Trigger download
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = path.split('/').pop() || 'archivo';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error(`Download error (${type}/${path}):`, error);
      throw error;
    }
  },

  // Download multiple files/folders as tar.gz
  downloadZip: async (type, paths, name = 'multimedia.tar.gz') => {
    const url = `${baseURL}/multimedia/download-zip/${type}`;
    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      },
      credentials: 'include',
      body: JSON.stringify({ paths, name })
    };

    try {
      const response = await fetch(url, config);
      if (!response.ok) {
        let errorMessage = 'Error descargando tar';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          // ignore
        }
        throw new Error(errorMessage);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
    } catch (error) {
      console.error(`Download tar error (${type}):`, error);
      throw error;
    }
  },

  // Upload file
  upload: async (type, path, file) => {
    logger.log('[API] Starting upload:', {
      type,
      path,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    });

    const formData = new FormData();
    formData.append('file', file);
    // Enviar variantes de la ruta por compatibilidad con backend
    const normalizedPath = path || '';
    formData.append('path', normalizedPath);
    formData.append('folder', normalizedPath);
    formData.append('targetPath', normalizedPath);

    const url = `${baseURL}/multimedia/upload/${type}${path ? `?path=${encodeURIComponent(path)}` : ''}`;
    const config = {
      method: 'POST',
      headers: {
        // Don't set Content-Type for FormData, let browser set it with boundary
        ...(token && { Authorization: `Bearer ${token}` })
      },
      credentials: 'include',
      body: formData
    };

    logger.log('[API] Upload URL:', url);

    try {
      const response = await fetch(url, config);

      logger.log('[API] Upload response received:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: {
          contentType: response.headers.get('content-type'),
          contentLength: response.headers.get('content-length')
        }
      });

      let data = null;
      const contentType = response.headers.get('content-type') || '';

      if (contentType.includes('application/json')) {
        data = await response.json();
        logger.log('[API] Upload response data:', data);
      } else {
        const text = await response.text();
        logger.log('[API] Upload response text:', text);
        data = text ? { message: text } : null;
      }

      const successFlag = data?.success;
      const errorMessage = data?.error || data?.message;

      if (!response.ok || successFlag === false) {
        const errorMsg = errorMessage || `Error ${response.status || ''}`.trim();
        console.error('[API] Upload failed:', errorMsg);
        throw new Error(errorMsg);
      }

      logger.log('[API] Upload successful');
      return data || { success: true };
    } catch (error) {
      console.error('[API] Upload error:', error);

      // Check if it's a network error
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        throw new Error('Upload failed: Network error - Cannot reach server');
      }

      // Check if it's a timeout
      if (error.name === 'AbortError') {
        throw new Error('Upload failed: Request timeout');
      }

      throw error;
    }
  },

  // Delete file or directory
  delete: (type, path) =>
    apiCall(`/multimedia/delete/${type}?path=${encodeURIComponent(path)}`, {
      method: 'DELETE'
    }),

  // Rename file or directory
  rename: (type, oldPath, newName) =>
    apiCall(`/multimedia/rename/${type}`, {
      method: 'PUT',
      body: JSON.stringify({ oldPath, newName })
    }),

  // Create directory
  createFolder: (type, parentPath, folderName) =>
    apiCall(`/multimedia/create-folder/${type}`, {
      method: 'POST',
      body: JSON.stringify({ parentPath, folderName })
    }),

  // Get file information
  getFileInfo: (type, path) => {
    const params = `?path=${encodeURIComponent(path)}`;
    return apiCall(`/multimedia/file-info/${type}${params}`);
  }
};

// --------- AGENT TRACKING (Seguimiento) ---------
const agentTracking = {
  // Obtener llamadas en seguimiento del agente
  getAgentTracking: (agentId, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/agent-tracking/agent/${agentId}${qs ? `?${qs}` : ''}`);
  },

  // Obtener un registro de tracking específico
  getTrackingById: (trackingId) => apiCall(`/agent-tracking/${trackingId}`),

  // Crear tracking al tipificar
  createTracking: (trackingData) => apiCall('/agent-tracking', {
    method: 'POST',
    body: JSON.stringify(trackingData)
  }),

  // Actualizar tags
  updateTags: (trackingId, tags) => apiCall(`/agent-tracking/${trackingId}/tags`, {
    method: 'PUT',
    body: JSON.stringify({ tags })
  }),

  // Actualizar datos del cliente
  updateClientData: (trackingId, payload) => apiCall(`/agent-tracking/${trackingId}/client`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  }),

  // Cambiar estado del tracking
  updateStatus: (trackingId, status) => apiCall(`/agent-tracking/${trackingId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status })
  }),

  // Incrementar contador de interacciones
  incrementInteractions: (trackingId) => apiCall(`/agent-tracking/${trackingId}/interaction`, {
    method: 'POST'
  }),

  // NOTAS
  addNote: (trackingId, noteData) => apiCall(`/agent-tracking/${trackingId}/notes`, {
    method: 'POST',
    body: JSON.stringify(noteData)
  }),

  updateNote: (noteId, noteData) => apiCall(`/agent-tracking/notes/${noteId}`, {
    method: 'PUT',
    body: JSON.stringify(noteData)
  }),

  deleteNote: (noteId) => apiCall(`/agent-tracking/notes/${noteId}`, {
    method: 'DELETE'
  }),

  // AGENDAMIENTOS
  getPendingSchedules: (agentId) => apiCall(`/agent-tracking/schedules/pending/${agentId}`),

  createSchedule: (trackingId, scheduleData) => apiCall(`/agent-tracking/${trackingId}/schedule`, {
    method: 'POST',
    body: JSON.stringify(scheduleData)
  }),

  updateScheduleStatus: (scheduleId, status) => apiCall(`/agent-tracking/schedules/${scheduleId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status })
  })
};

// --------- CALL HISTORY (Historial completo) ---------
const callHistory = {
  // Obtener historial completo del agente
  getAgentHistory: (agentId, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/call-history/agent/${agentId}${qs ? `?${qs}` : ''}`);
  },

  // Obtener detalles de una llamada
  getCallDetails: (callId) => apiCall(`/call-history/${callId}`),

  // Obtener estadísticas del historial
  getAgentStats: (agentId, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/call-history/agent/${agentId}/stats${qs ? `?${qs}` : ''}`);
  }
};

// --------- AGENT TAGS (cached) ----------
const agentTags = {
  getAll: async (agentId, { forceRefresh = false } = {}) => {
    const key = `cti_agent_tags_${agentId}`;

    if (!forceRefresh) {
      try {
        const cached = sessionStorage.getItem(key);
        if (cached) {
          logger.log('[AGENT-TAGS] Datos obtenidos desde sessionStorage (CACHÉ)', { agentId, cacheKey: key });
          return { success: true, data: JSON.parse(cached), fromCache: true };
        }
      } catch (e) {
        logger.warn('[AGENT-TAGS] Error leyendo cache', e);
      }
    }

    const result = await apiCall(`/agent-tags/${agentId}`);
    if (result?.success && result?.data) {
      try {
        sessionStorage.setItem(key, JSON.stringify(result.data));
        logger.log('[AGENT-TAGS] Datos guardados en sessionStorage', { agentId, cacheKey: key });
      } catch (e) {
        logger.warn('[AGENT-TAGS] Error guardando cache', e);
      }
    }
    return result;
  },
  invalidate: (agentId) => {
    try {
      sessionStorage.removeItem(`cti_agent_tags_${agentId}`);
      logger.log('[AGENT-TAGS] Cache invalidado', { agentId });
    } catch (e) { /* ignore */ }
  }
};

// --------- WALL (Muro) ---------
const wall = {
  getPosts: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiCall(`/wall/posts${qs ? `?${qs}` : ''}`);
  },
  createPost: async (formData) => {
    const url = `${baseURL}/wall/posts`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      credentials: 'include',
      body: formData
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Error al crear post' }));
      throw new Error(err.message);
    }
    return response.json();
  },
  updatePost: async (id, formData) => {
    const url = `${baseURL}/wall/posts/${id}`;
    const response = await fetch(url, {
      method: 'PUT',
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      credentials: 'include',
      body: formData
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Error al editar post' }));
      throw new Error(err.message);
    }
    return response.json();
  },
  deletePost: (id) => apiCall(`/wall/posts/${id}`, { method: 'DELETE' }),
  toggleLike: (id) => apiCall(`/wall/posts/${id}/like`, { method: 'POST' }),
  togglePin: (id) => apiCall(`/wall/posts/${id}/pin`, { method: 'POST' }),
  getLikes: (id) => apiCall(`/wall/posts/${id}/likes`),
  getComments: (id) => apiCall(`/wall/posts/${id}/comments`),
  addComment: (id, content) => apiCall(`/wall/posts/${id}/comments`, { method: 'POST', body: JSON.stringify({ content }) }),
  deleteComment: (commentId) => apiCall(`/wall/comments/${commentId}`, { method: 'DELETE' }),
  getCampaigns: () => apiCall('/wall/campaigns'),
  getUnreadCount: () => apiCall('/wall/unread-count'),
  markRead: () => apiCall('/wall/mark-read', { method: 'POST' })
};

const im = {
  getConversations: (params) => apiCall(`/im/conversations${params ? '?' + new URLSearchParams(params) : ''}`),
  getAllConversations: (params) => apiCall(`/im/conversations/all${params ? '?' + new URLSearchParams(params) : ''}`),
  createConversation: (data) => apiCall('/im/conversations', { method: 'POST', body: JSON.stringify(data) }),
  getMessages: (convId, params) => apiCall(`/im/conversations/${convId}/messages${params ? '?' + new URLSearchParams(params) : ''}`),
  sendMessage: async (convId, formData) => {
    const url = `${baseURL}/im/conversations/${convId}/messages`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      credentials: 'include',
      body: formData
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Error al enviar mensaje' }));
      throw new Error(err.message);
    }
    return response.json();
  },
  markRead: (convId) => apiCall(`/im/conversations/${convId}/read`, { method: 'POST' }),
  updateConversation: async (convId, formData) => {
    const url = `${baseURL}/im/conversations/${convId}`;
    const response = await fetch(url, {
      method: 'PUT',
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      credentials: 'include',
      body: formData
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Error al actualizar' }));
      throw new Error(err.message);
    }
    return response.json();
  },
  getUnreadCount: () => apiCall('/im/unread-count'),
  getAgents: () => apiCall('/im/agents'),
  getOnlineAgents: () => apiCall('/im/online'),
  getParticipants: (convId) => apiCall(`/im/conversations/${convId}/participants`),
  addParticipants: (convId, agents) => apiCall(`/im/conversations/${convId}/participants`, { method: 'POST', body: JSON.stringify({ agents }) }),
  removeParticipant: (convId, agentId) => apiCall(`/im/conversations/${convId}/participants/${agentId}`, { method: 'DELETE' }),
};

const value = {
  baseURL,
  apiCall,
  agents,
  skills,
  extensions,
  datastructures,
  campaigns,
  trunks,
  amd,
  dialplans,
  loads,
  dialer,
  supervisor,
  simulator,
  softphone,
  crmConfigs,
  ends,
  roles,
  workflows,
  sse,
  heartbeat,
  systemConfig,
  multimedia,
  agentTracking,
  callHistory,
  agentTags,
  apiTokens,
  webhooks: webhooksApi,
  wall,
  im
};

  return (
    <ApiContext.Provider value={value}>
      {children}
    </ApiContext.Provider>
  );
};

export const useApi = () => {
  const context = useContext(ApiContext);
  if (!context) throw new Error('useApi must be used within an ApiProvider');
  return context;
};
